import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { getApiErrorMessage, getModels, updateModelAccountDescription } from '../../api'
import { listConfigs } from '../../api/graphConfigs'
import { getAgencySafeConfig, updateAgencySafeConfig } from '../../api/graph_settings'
import { EmptyState, LoadingBlock, MetricCard, Section, StatusPill } from '../../components/v2/V2Primitives'
import ModelBiblePanel from '../../components/ModelBiblePanel'
import CustomPricingPreview, {
  CUSTOM_CAPABILITY_BOOLEAN_SETTING_KEYS,
  CUSTOM_CAPABILITY_CHECKLIST_SETTING_KEYS,
  CUSTOM_CAPABILITY_GROUPS,
  CUSTOM_CAPABILITY_LIST_SETTING_KEYS,
  CUSTOM_NUDITY_OPTIONS,
  CUSTOM_PRICE_SETTING_KEYS,
  CUSTOM_PROP_OPTIONS,
  withCustomPricingDefaults,
} from '../../components/CustomPricingPreview'
import MonetizationRoutesSettings, {
  MONETIZATION_ROUTE_BOOLEAN_SETTING_KEYS,
  MONETIZATION_ROUTE_STRING_SETTING_KEYS,
  withMonetizationRouteDefaults,
} from '../../components/MonetizationRoutesSettings'
import AlternativeMonetizationSettings, {
  ALTERNATIVE_MONETIZATION_BOOLEAN_KEYS,
  ALTERNATIVE_MONETIZATION_STRING_KEYS,
  withAlternativeMonetizationDefaults,
} from '../../components/AlternativeMonetizationSettings'
import { useI18n } from '../../i18n'
import { getStoredAuthRole, isAdminRole } from '../../lib/realm'
import { useWorkspaceScope } from '../../contexts/WorkspaceScopeContext'
import { asList, modelTitle } from './v2Utils'

const AUTOMATION_MODE_OPTIONS = [
  ['full_auto', 'Full auto'],
  ['handoff_on_offer', 'Handoff on offer'],
  ['handoff_after_first_purchase', 'Handoff after first purchase'],
]

const TELEGRAM_ALERT_EVENTS = [
  'purchase',
  'state_changed',
  'low_balance',
  'handoff_on_offer',
  'handoff_after_first_purchase',
  'insufficient_credit',
  'graph_failed',
  'openai_timeout',
]

const BOOLEAN_SETTING_KEYS = new Set([
  'proactive_skip_test_fans',
  'proactive_webhook_users_online_enabled',
  'proactive_webhook_users_typing_enabled',
  'proactive_webhook_posts_liked_enabled',
  'proactive_webhook_posts_commented_enabled',
  'history_reactivation_enabled',
  'history_reactivation_dry_run',
  'history_reactivation_include_active_free',
  'history_reactivation_include_free_with_history',
  'history_reactivation_free_auto_approve',
  'history_reactivation_auto_dispatch_enabled',
  'prompt_language_match_fan',
  'custom_requests_enabled',
  'custom_pricing_enabled',
  ...CUSTOM_CAPABILITY_BOOLEAN_SETTING_KEYS,
  'timewaster_strategy_enabled',
  ...MONETIZATION_ROUTE_BOOLEAN_SETTING_KEYS,
  ...ALTERNATIVE_MONETIZATION_BOOLEAN_KEYS,
])

const LIST_SETTING_KEYS = new Set([
  'proactive_webhook_custom_events',
  'prompt_allowed_languages',
  'prompt_disallowed_topics',
  ...CUSTOM_CAPABILITY_LIST_SETTING_KEYS,
])
const CHECKLIST_SETTING_KEYS = new Set(CUSTOM_CAPABILITY_CHECKLIST_SETTING_KEYS)

const BASIC_SETTING_KEYS = [
  'prompt_language_match_fan',
  'prompt_primary_language',
  'prompt_allowed_languages',
  'prompt_tone_style',
  'prompt_emoji_level',
  'prompt_message_length',
  'prompt_disallowed_topics',
  'prompt_custom_safe_instructions',
]

const ADVANCED_GROUPS = [
  {
    title: 'Proactive cadence',
    description: 'Timing and dispatch limits for proactive follow-ups.',
    keys: [
      'proactive_message_generator_conversation_messages_count',
      'proactive_followup_max_dispatch_per_tick',
      'proactive_skip_test_fans',
      'proactive_cannot_send_retry_minutes',
      'proactive_daily_touch_max_hours',
      'proactive_recent_continuation_max_days',
      'proactive_warm_reactivation_max_days',
    ],
  },
  {
    title: 'Provider event triggers',
    description: 'Timing safeguards for provider event rules. Event selection is configured in proactive rule conditions.',
    keys: [
      'proactive_webhook_min_silence_minutes',
      'proactive_webhook_cooldown_minutes',
    ],
  },
  {
    title: 'History reactivation',
    description: 'Bulk import, candidate approval and retry rules for old chats.',
    keys: [
      'history_reactivation_enabled',
      'history_reactivation_dry_run',
      'history_reactivation_batch_size',
      'history_reactivation_scan_max_pages',
      'history_reactivation_include_active_free',
      'history_reactivation_include_free_with_history',
      'history_reactivation_free_auto_approve',
      'history_reactivation_auto_dispatch_enabled',
      'history_reactivation_auto_dispatch_limit',
      'history_reactivation_messages_limit',
      'history_reactivation_min_silence_hours',
      'history_reactivation_max_age_days',
      'history_reactivation_dispatch_limit',
      'history_reactivation_max_attempts',
      'history_reactivation_retry_minutes',
    ],
  },
  {
    title: 'Timewaster handling',
    description: 'Slow long unpaid high-tension conversations and apply a playful value boundary. Disabled by default.',
    keys: [
      'timewaster_strategy_enabled',
      'timewaster_min_fan_messages',
      'timewaster_high_tension_min',
      'timewaster_low_spend_intent_max',
      'timewaster_reply_delay_min_seconds',
      'timewaster_reply_delay_max_seconds',
    ],
  },
]

const ADVANCED_TOOL_LINKS = {
  l4: {
    title: 'L4 relationship variables',
    description: 'Business-level variables used by proactive rules, transitions and monetization gates.',
    href: '/v2/agency-settings?section=l4',
    action: 'Open L4 editor',
    bullets: ['Define computed variables', 'Use L2/L3 context in conditions', 'Expose stable keys to rule builders'],
  },
  reactivationQueue: {
    title: 'Reactivation queue',
    description: 'Review old-chat candidates, approve or skip them, and dispatch approved reactivation messages.',
    href: '/v2/agency-settings?section=reactivation',
    action: 'Open reactivation queue',
    bullets: ['Run discovery', 'Approve or skip candidates', 'Dispatch approved conversations'],
  },
  transitions: {
    title: 'State transitions',
    description: 'Configure graph transition conditions such as monetization gates and scripted flow switches.',
    href: '/v2/agency-settings?section=transitions',
    action: 'Open transitions',
    bullets: ['Edit transition keys', 'Build AND/OR conditions', 'Preview monetization gates'],
  },
  conditionalPrompts: {
    title: 'Conditional prompts',
    description: 'Add prompt fragments that apply only when fan context and L4 conditions match.',
    href: '/v2/agency-settings?section=conditionalPrompts',
    action: 'Open conditional prompts',
    bullets: ['Message and media-reply rules', 'Offer generator rules', 'Proactive generator rules'],
  },
}

const SETTING_META = {
  prompt_language_match_fan: {
    label: 'Match fan language',
    description: 'When enabled, replies keep the language used by the fan.',
    recommended: 'Enabled for most live models.',
    type: 'boolean',
    risk: 'Low',
  },
  prompt_primary_language: {
    label: 'Primary language',
    description: 'Fallback language when the fan language is unknown.',
    recommended: 'Use the agency operating language.',
    type: 'text',
    placeholder: 'en',
    risk: 'Low',
  },
  prompt_allowed_languages: {
    label: 'Allowed languages',
    description: 'Comma-separated list of languages the bot may use.',
    recommended: 'Keep narrow until multilingual QA is complete.',
    type: 'list',
    placeholder: 'en, es, de',
    risk: 'Medium',
  },
  prompt_tone_style: {
    label: 'Tone style',
    description: 'Default communication style used by message generation.',
    recommended: 'Specific, concise style notes rather than generic persona text.',
    type: 'textarea',
    risk: 'Medium',
  },
  prompt_emoji_level: {
    label: 'Emoji level',
    description: 'Controls how often the bot uses emoji-like expressive markers.',
    recommended: 'auto',
    type: 'select',
    options: [['auto', 'Auto'], ['none', 'None'], ['low', 'Low'], ['medium', 'Medium'], ['high', 'High']],
    risk: 'Low',
  },
  prompt_message_length: {
    label: 'Message length',
    description: 'Default answer length preference.',
    recommended: 'auto',
    type: 'select',
    options: [['auto', 'Auto'], ['short', 'Short'], ['medium', 'Medium'], ['long', 'Long']],
    risk: 'Low',
  },
  prompt_disallowed_topics: {
    label: 'Disallowed topics',
    description: 'Comma-separated topics the bot should avoid.',
    recommended: 'Keep explicit and reviewed by operations.',
    type: 'list',
    risk: 'High',
  },
  prompt_custom_safe_instructions: {
    label: 'Safety instructions',
    description: 'Agency-level guardrails appended to safe prompts.',
    recommended: 'Short operational rules, no contradictory instructions.',
    type: 'textarea',
    risk: 'High',
  },
  timewaster_strategy_enabled: {
    label: 'Enable timewaster handling',
    description: 'Applies only to fan-initiated, long, unpaid conversations with high tension and low spend intent.',
    recommended: 'Disabled until thresholds and copy are reviewed for the model.',
    type: 'boolean',
    risk: 'High',
  },
  timewaster_min_fan_messages: {
    label: 'Fan-message threshold',
    description: 'Minimum number of incoming fan messages before this strategy may activate.',
    recommended: '100 messages.',
    type: 'number',
    min: 1,
    max: 10000,
    risk: 'Medium',
  },
  timewaster_high_tension_min: {
    label: 'High tension minimum',
    description: 'Minimum sexual tension score required to use the value-boundary prompt.',
    recommended: '70 out of 100.',
    type: 'number',
    min: 0,
    max: 100,
    risk: 'Medium',
  },
  timewaster_low_spend_intent_max: {
    label: 'Low spend intent maximum',
    description: 'Highest spend intent score still treated as low purchase intent.',
    recommended: '25 out of 100.',
    type: 'number',
    min: 0,
    max: 100,
    risk: 'Medium',
  },
  timewaster_reply_delay_min_seconds: {
    label: 'Minimum slowed reply',
    description: 'Lower bound of the random reply delay while this strategy is active.',
    recommended: '120 seconds.',
    type: 'number',
    min: 0,
    max: 3600,
    risk: 'Medium',
  },
  timewaster_reply_delay_max_seconds: {
    label: 'Maximum slowed reply',
    description: 'Upper bound of the random reply delay while this strategy is active.',
    recommended: '480 seconds.',
    type: 'number',
    min: 0,
    max: 3600,
    risk: 'Medium',
  },
  proactive_message_generator_conversation_messages_count: {
    label: 'Proactive context messages',
    description: 'How many recent messages are sent into proactive generation context.',
    recommended: 'Enough context for tone without overloading the model.',
    type: 'number',
    min: 1,
    risk: 'Medium',
  },
  proactive_followup_max_dispatch_per_tick: {
    label: 'Max dispatch per tick',
    description: 'Upper bound for proactive messages sent in one scheduler pass.',
    recommended: 'Start low, raise after provider stability is proven.',
    type: 'number',
    min: 0,
    risk: 'High',
  },
  proactive_skip_test_fans: {
    label: 'Skip test fans',
    description: 'Prevents proactive jobs from sending to test chats.',
    recommended: 'Enabled in production.',
    type: 'boolean',
    risk: 'Low',
  },
  proactive_cannot_send_retry_minutes: {
    label: 'Cannot-send retry minutes',
    description: 'Wait time before retrying after a provider cannot-send result.',
    recommended: 'Avoid aggressive retries.',
    type: 'number',
    min: 1,
    risk: 'Medium',
  },
  proactive_daily_touch_max_hours: {
    label: 'Daily touch max hours',
    description: 'Upper window for daily touch cadence.',
    recommended: 'Keep aligned with quiet hours and model timezone.',
    type: 'number',
    min: 1,
    risk: 'Medium',
  },
  proactive_recent_continuation_max_days: {
    label: 'Recent continuation days',
    description: 'How long a recent chat remains eligible for continuation.',
    recommended: 'Shorter windows feel more natural.',
    type: 'number',
    min: 0,
    risk: 'Medium',
  },
  proactive_warm_reactivation_max_days: {
    label: 'Warm reactivation days',
    description: 'How long warm fans stay eligible for reactivation.',
    recommended: 'Use only with tested scripts.',
    type: 'number',
    min: 0,
    risk: 'High',
  },
  proactive_webhook_users_online_enabled: { label: 'Online event trigger', description: 'React to users-online provider events.', recommended: 'Enable only when provider data is stable.', type: 'boolean', risk: 'Medium' },
  proactive_webhook_users_typing_enabled: { label: 'Typing event trigger', description: 'React to users-typing provider events.', recommended: 'Usually disabled until tested.', type: 'boolean', risk: 'Medium' },
  proactive_webhook_posts_liked_enabled: { label: 'Post liked trigger', description: 'React when fans like posts.', recommended: 'Enable for proven engagement flows.', type: 'boolean', risk: 'Medium' },
  proactive_webhook_posts_commented_enabled: { label: 'Post commented trigger', description: 'React when fans comment on posts.', recommended: 'Enable only with safe reply strategy.', type: 'boolean', risk: 'Medium' },
  proactive_webhook_min_silence_minutes: { label: 'Minimum silence minutes', description: 'Minimum silence before event-based proactive messages.', recommended: 'Prevents spammy immediate replies.', type: 'number', min: 0, risk: 'High' },
  proactive_webhook_cooldown_minutes: { label: 'Webhook cooldown minutes', description: 'Cooldown after a webhook-driven message.', recommended: 'Keep conservative.', type: 'number', min: 0, risk: 'High' },
  proactive_webhook_custom_events: { label: 'Custom webhook events', description: 'Comma-separated extra provider events.', recommended: 'Only add documented events.', type: 'list', risk: 'High' },
  history_reactivation_enabled: { label: 'History reactivation enabled', description: 'Allows old conversations to enter reactivation workflows.', recommended: 'Enable only after dry-run review.', type: 'boolean', risk: 'High' },
  history_reactivation_dry_run: { label: 'Dry run', description: 'Discovers candidates without dispatching messages.', recommended: 'Enabled before any production run.', type: 'boolean', risk: 'Low' },
  history_reactivation_batch_size: { label: 'Batch size', description: 'How many conversations are scanned per batch.', recommended: 'Small batches reduce provider pressure.', type: 'number', min: 1, risk: 'Medium' },
  history_reactivation_scan_max_pages: { label: 'Scan max pages', description: 'Maximum provider pages scanned per run.', recommended: 'Start low.', type: 'number', min: 1, risk: 'Medium' },
  history_reactivation_include_active_free: { label: 'Include active free', description: 'Includes active free fans in discovery.', recommended: 'Only if strategy covers free fans.', type: 'boolean', risk: 'Medium' },
  history_reactivation_include_free_with_history: { label: 'Include free with history', description: 'Includes free fans with previous chat history.', recommended: 'Useful for tested reactivation scripts.', type: 'boolean', risk: 'Medium' },
  history_reactivation_free_auto_approve: { label: 'Free auto approve', description: 'Auto-approves free fan candidates.', recommended: 'Keep disabled until QA confirms quality.', type: 'boolean', risk: 'High' },
  history_reactivation_auto_dispatch_enabled: { label: 'Auto dispatch', description: 'Allows approved candidates to be messaged automatically.', recommended: 'Disabled until operator review is proven.', type: 'boolean', risk: 'High' },
  history_reactivation_auto_dispatch_limit: { label: 'Auto dispatch limit', description: 'Maximum auto-dispatched messages per run.', recommended: 'Use a hard cap.', type: 'number', min: 0, risk: 'High' },
  history_reactivation_messages_limit: { label: 'Messages to import', description: 'How many old messages are imported for context.', recommended: 'Enough to understand history, not entire archive.', type: 'number', min: 1, risk: 'Medium' },
  history_reactivation_min_silence_hours: { label: 'Minimum silence hours', description: 'Minimum silence before a fan is eligible.', recommended: 'Avoid contacting recently active fans.', type: 'number', min: 0, risk: 'Medium' },
  history_reactivation_max_age_days: { label: 'Maximum age days', description: 'Oldest conversations eligible for reactivation.', recommended: 'Keep within a sensible relationship window.', type: 'number', min: 0, risk: 'Medium' },
  history_reactivation_dispatch_limit: { label: 'Dispatch limit', description: 'Manual dispatch cap per run.', recommended: 'Use a cap for provider safety.', type: 'number', min: 0, risk: 'High' },
  history_reactivation_max_attempts: { label: 'Max attempts', description: 'Retry attempts for failed candidates.', recommended: 'Low retry count.', type: 'number', min: 0, risk: 'Medium' },
  history_reactivation_retry_minutes: { label: 'Retry minutes', description: 'Delay between reactivation retries.', recommended: 'Avoid rapid retries.', type: 'number', min: 1, risk: 'Medium' },
  custom_requests_enabled: { label: 'Allow custom requests', description: 'Enables the made-to-order photo, video and audio request route for this model config.', recommended: 'Keep enabled only for models that accept customs.', type: 'boolean', risk: 'High' },
  custom_capabilities_configured: { label: 'Enforce production capabilities', description: 'Makes the formats, limits and boundaries below authoritative for this model config.', recommended: 'Fill the complete capability matrix first, then enable enforcement.', type: 'boolean', risk: 'High' },
  custom_photo_enabled: { label: 'Custom photos', description: 'Allows made-to-order photo sets for this model.', recommended: 'Enable only when the model can currently produce them.', type: 'boolean', risk: 'High' },
  custom_video_enabled: { label: 'Custom videos', description: 'Allows made-to-order video content for this model.', recommended: 'Enable only when production is available.', type: 'boolean', risk: 'High' },
  custom_audio_enabled: { label: 'Custom audio / voice', description: 'Allows made-to-order audio and voice notes for this model.', recommended: 'Keep disabled without a real recording workflow.', type: 'boolean', risk: 'High' },
  personalized_audio_fantasy_enabled: { label: 'Shared fantasy audio path', description: 'Lets the bot develop a fan fantasy through conversation and softly suggest a personalized spoken story when the custom-audio capability is available.', recommended: 'Enable only with custom audio and a real operator fulfillment workflow.', type: 'boolean', risk: 'High' },
  personalized_audio_fantasy_min_words: { label: 'Minimum developed fantasy words', description: 'Minimum message length used when sexual context, rather than an explicit fantasy word, establishes a developed fantasy.', recommended: '18 words avoids treating a short sexual line as a custom opportunity.', type: 'number', min: 4, max: 200, step: 1, risk: 'Medium' },
  personalized_audio_fantasy_soft_offer_after_signals: { label: 'Signals before audio suggestion', description: 'Number of fan fantasy contributions required before the bot may make one soft personalized-audio suggestion.', recommended: '2 signals gives the fan room to co-create before monetization.', type: 'number', min: 1, max: 10, step: 1, risk: 'Medium' },
  custom_name_personalization_enabled: { label: 'Name personalization', description: 'Allows the fan name to be spoken, written or otherwise included.', recommended: 'Review privacy and production workflow before enabling.', type: 'boolean', risk: 'Medium' },
  custom_face_allowed: { label: 'Face may be shown', description: 'Allows the model face to appear in made-to-order content.', recommended: 'Disabled unless the model explicitly approved it.', type: 'boolean', risk: 'High' },
  custom_external_items_allowed: { label: 'May source new items', description: 'Allows customs that require buying or sourcing a new prop, outfit or consumable.', recommended: 'Disabled unless operators can verify costs and availability.', type: 'boolean', risk: 'High' },
  custom_outfit_purchase_allowed: { label: 'May purchase a requested outfit', description: 'Allows an outfit to be bought specifically for the fan custom, with the confirmed cost added to the order.', recommended: 'Enable only with an operator purchasing workflow.', type: 'boolean', risk: 'High' },
  custom_prop_purchase_allowed: { label: 'May purchase a requested prop', description: 'Allows a missing prop to be sourced specifically for the custom.', recommended: 'Require cost confirmation before quoting.', type: 'boolean', risk: 'High' },
  custom_max_purchase_budget: { label: 'Maximum purchase budget', description: 'Hard cap for confirmed outfit or prop expenses. Zero means no preset cap and requires manual cost review.', recommended: 'Use a conservative amount in the pricing currency.', type: 'number', min: 0, max: 100000, step: 0.01, risk: 'High' },
  custom_max_nudity_level: { label: 'Maximum nudity level', description: 'Highest visual explicitness the model approved for custom production.', recommended: 'Choose the exact approved boundary; higher prices never override it.', type: 'select', options: CUSTOM_NUDITY_OPTIONS, risk: 'High' },
  custom_toy_use_allowed: { label: 'Toy use allowed', description: 'Allows adult toys to appear or be used in the custom.', recommended: 'Enable only with explicit model approval.', type: 'boolean', risk: 'High' },
  custom_toy_insertion_allowed: { label: 'Toy insertion allowed', description: 'Separately allows insertion with an adult toy. Requires Toy use allowed.', recommended: 'Keep separate from general toy use.', type: 'boolean', risk: 'High' },
  custom_other_object_insertion_allowed: { label: 'Other-object insertion allowed', description: 'Allows insertion with a non-toy object approved for production.', recommended: 'Keep disabled unless explicitly approved.', type: 'boolean', risk: 'High' },
  custom_gg_content_allowed: { label: 'GG partner content', description: 'Allows made-to-order girl-girl content with another performer.', recommended: 'Enable only when a partner and production workflow are available.', type: 'boolean', risk: 'High' },
  custom_bg_content_allowed: { label: 'BG partner content', description: 'Allows made-to-order boy-girl content with another performer.', recommended: 'Enable only when a partner and production workflow are available.', type: 'boolean', risk: 'High' },
  custom_max_photo_count: { label: 'Maximum photos per custom', description: 'Hard limit enforced before a quote can be produced.', recommended: 'Match the largest set the model can reliably deliver.', type: 'number', min: 1, max: 1000, step: 1, risk: 'Medium' },
  custom_max_video_minutes: { label: 'Maximum video minutes', description: 'Hard duration limit for one custom video.', recommended: 'Use a realistic production maximum.', type: 'number', min: 1, max: 1000, step: 0.5, risk: 'Medium' },
  custom_max_audio_minutes: { label: 'Maximum audio minutes', description: 'Hard duration limit for one custom audio or voice note.', recommended: 'Use a realistic recording maximum.', type: 'number', min: 1, max: 1000, step: 0.5, risk: 'Medium' },
  custom_standard_turnaround_days: { label: 'Standard turnaround, days', description: 'The normal production window supplied to custom guidance; it is not a guaranteed deadline.', recommended: 'Include operator and model availability.', type: 'number', min: 1, max: 365, step: 1, risk: 'Medium' },
  custom_allowed_scenarios: { label: 'Approved scenario ideas', description: 'Comma-separated model-approved themes the bot may use when shaping a custom idea. Empty means no curated ideas are recorded.', recommended: 'Use short reusable themes, not full prompt prose.', type: 'list', risk: 'High' },
  custom_forbidden_scenarios: { label: 'Forbidden scenarios', description: 'Comma-separated hard boundaries. Matching requests are declined before pricing.', recommended: 'Record every production and persona boundary explicitly.', type: 'list', risk: 'High' },
  custom_available_outfits: { label: 'Available outfits', description: 'Outfits the bot may truthfully mention for custom production.', recommended: 'List only items currently available to the model.', type: 'list', risk: 'High' },
  custom_available_locations: { label: 'Available locations', description: 'Locations the bot may truthfully use in a custom concept.', recommended: 'Avoid revealing private or identifying locations.', type: 'list', risk: 'High' },
  custom_available_prop_categories: { label: 'Available props', description: 'Select the standard props already available without a new purchase.', recommended: 'Keep this checklist current.', type: 'checklist', options: CUSTOM_PROP_OPTIONS, risk: 'High' },
  custom_available_props: { label: 'Other available props', description: 'Comma-separated props not covered by the checklist above.', recommended: 'List only items currently available.', type: 'list', risk: 'High' },
  custom_pricing_enabled: { label: 'Automatic custom pricing', description: 'Allows the bot to calculate and negotiate custom orders from these rules.', recommended: 'Enable after rates are reviewed for this model config.', type: 'boolean', risk: 'High' },
  custom_pricing_currency: { label: 'Currency', description: 'Currency used for custom quotes and tip deposits.', recommended: 'Match the platform account currency.', type: 'select', options: [['USD', 'USD'], ['EUR', 'EUR'], ['GBP', 'GBP']], risk: 'Medium' },
  custom_pricing_base_fee: { label: 'Base production fee', description: 'Fixed amount added to every custom before per-unit rates.', recommended: 'Covers setup and administration.', type: 'number', min: 0, max: 100000, step: 0.01, risk: 'Medium' },
  custom_pricing_minimum_total: { label: 'Minimum order', description: 'Absolute floor for any custom order.', recommended: 'Never set below operational cost.', type: 'number', min: 0, max: 100000, step: 0.01, risk: 'High' },
  custom_pricing_video_per_minute: { label: 'Video price per minute', description: 'Base rate multiplied by requested video length.', recommended: 'Review against production effort.', type: 'number', min: 0, max: 100000, step: 0.01, risk: 'High' },
  custom_pricing_audio_per_minute: { label: 'Audio price per minute', description: 'Base rate multiplied by requested voice/audio length.', recommended: 'Use a lower rate only when effort is lower.', type: 'number', min: 0, max: 100000, step: 0.01, risk: 'Medium' },
  custom_pricing_photo_each: { label: 'Price per photo', description: 'Base rate multiplied by requested photo count.', recommended: 'Include shooting and selection effort.', type: 'number', min: 0, max: 100000, step: 0.01, risk: 'High' },
  custom_pricing_name_markup_percent: { label: 'Name personalization markup', description: 'Percentage added when the custom includes the fan name.', recommended: '20%.', type: 'number', min: 0, max: 500, step: 1, risk: 'Medium' },
  custom_pricing_urgency_72h_percent: { label: '72-hour urgency markup', description: 'Percentage added for delivery within 72 hours.', recommended: 'Lower than the 24-hour markup.', type: 'number', min: 0, max: 500, step: 1, risk: 'Medium' },
  custom_pricing_urgency_24h_percent: { label: '24-hour urgency markup', description: 'Percentage added for delivery within 24 hours.', recommended: 'Must be at least the 72-hour markup.', type: 'number', min: 0, max: 500, step: 1, risk: 'High' },
  custom_pricing_negotiation_margin_percent: { label: 'Negotiation margin', description: 'Opening price markup above the protected minimum. The bot may negotiate inside this range.', recommended: '15%.', type: 'number', min: 0, max: 500, step: 1, risk: 'High' },
  custom_pricing_minimum_deposit_percent: { label: 'Minimum tip deposit', description: 'Share of the agreed total requested as a platform tip before production.', recommended: 'At least 50%.', type: 'number', min: 50, max: 100, step: 1, risk: 'High' },
}

export default function AdminV2Settings() {
  const {
    selectedModelId: workspaceModelId,
    selectedModel: workspaceModel,
    setSelectedModelId: setWorkspaceModelId,
  } = useWorkspaceScope()
  const [tab, setTab] = useState('basic')
  const [basicSection, setBasicSection] = useState('model')
  const [advancedSection, setAdvancedSection] = useState('proactive')
  const [loading, setLoading] = useState(true)
  const [savingModel, setSavingModel] = useState(false)
  const [savingConfig, setSavingConfig] = useState(false)
  const [error, setError] = useState('')
  const [status, setStatus] = useState('')
  const [models, setModels] = useState([])
  const [selectedModelId, setSelectedModelId] = useState('')
  const [modelDrafts, setModelDrafts] = useState({})
  const [configs, setConfigs] = useState([])
  const [selectedConfigId, setSelectedConfigId] = useState('')
  const [safeConfig, setSafeConfig] = useState(null)
  const [settingsDraft, setSettingsDraft] = useState({})
  const [warnings, setWarnings] = useState([])
  const configLoadSequenceRef = useRef(0)
  const lastWorkspaceSyncRef = useRef('')
  const currentRole = getStoredAuthRole()
  const canManageModelDefaultConfig = isAdminRole(currentRole) || currentRole === 'tester_plus'

  useEffect(() => {
    let alive = true
    async function load() {
      setLoading(true)
      setError('')
      try {
        const [modelsResult, configsResult, configResult] = await Promise.all([
          getModels(),
          canManageModelDefaultConfig ? listConfigs() : Promise.resolve([]),
          getAgencySafeConfig(workspaceModel?.default_config_id ?? null),
        ])
        if (!alive) return
        const rows = asList(modelsResult)
        setModels(rows)
        setModelDrafts(Object.fromEntries(rows.map((model) => [model.id, initModelDraft(model)])))
        const scopedModelExists = rows.some((model) => String(model.id) === String(workspaceModelId))
        setSelectedModelId(scopedModelExists ? String(workspaceModelId) : String(rows[0]?.id || ''))
        setConfigs(Array.isArray(configsResult) ? configsResult : asList(configsResult))
        applyConfig(configResult)
      } catch (err) {
        if (alive) setError(getApiErrorMessage(err, 'Failed to load settings'))
      } finally {
        if (alive) setLoading(false)
      }
    }
    load()
    return () => { alive = false }
  }, [])

  function applyConfig(configResponse, fallbackConfigId = '') {
    const config = configResponse?.config || configResponse
    setSafeConfig(config)
    setSelectedConfigId(String(config?.id || fallbackConfigId || ''))
    const nextSettings = withAlternativeMonetizationDefaults(withMonetizationRouteDefaults(withCustomPricingDefaults(config?.settings || {})))
    for (const key of LIST_SETTING_KEYS) {
      if (key in nextSettings) nextSettings[key] = toCsv(nextSettings[key])
    }
    setSettingsDraft(nextSettings)
  }

  const selectedModel = useMemo(
    () => models.find((model) => String(model.id) === String(selectedModelId)),
    [models, selectedModelId],
  )
  const selectedDraft = selectedModel ? (modelDrafts[selectedModel.id] || initModelDraft(selectedModel)) : null
  const activeConfigName = safeConfig?.name || configs.find((config) => String(config.id) === String(selectedConfigId))?.name || 'Active config'
  const configuredModels = models.filter((model) => String(model.account_description || '').trim() && String(model.timezone || '').trim()).length
  const pausedModels = models.filter((model) => readBool(model.bot_paused)).length

  async function handleConfigChange(value) {
    const requestSequence = ++configLoadSequenceRef.current
    setSelectedConfigId(value || '')
    setStatus('')
    setError('')
    setLoading(true)
    try {
      const result = await getAgencySafeConfig(value || null)
      if (requestSequence !== configLoadSequenceRef.current) return
      applyConfig(result, value || '')
    } catch (err) {
      if (requestSequence === configLoadSequenceRef.current) {
        setError(getApiErrorMessage(err, 'Failed to load selected config'))
      }
    } finally {
      if (requestSequence === configLoadSequenceRef.current) setLoading(false)
    }
  }

  async function handleCustomModelChange(value) {
    const nextModelId = String(value || '')
    setSelectedModelId(nextModelId)
    setWorkspaceModelId(nextModelId)
    const nextModel = models.find((model) => String(model.id) === nextModelId)
    const activeConfig = configs.find((config) => config.is_active)
    const nextConfigId = String(nextModel?.default_config_id ?? activeConfig?.id ?? '')
    lastWorkspaceSyncRef.current = `${nextModelId}:${nextConfigId}`
    await handleConfigChange(nextConfigId)
  }

  useEffect(() => {
    if (loading) return
    // "All models" is a valid global workspace scope, but this page edits
    // model-owned settings and must keep a concrete local selection.
    if (!workspaceModelId) return
    const nextModelId = String(workspaceModelId || '')
    const nextModel = models.find((model) => String(model.id) === nextModelId)
    const activeConfig = configs.find((config) => config.is_active)
    const nextConfigId = String(nextModel?.default_config_id ?? activeConfig?.id ?? '')
    const syncKey = `${nextModelId}:${nextConfigId}`
    if (lastWorkspaceSyncRef.current === syncKey) return
    lastWorkspaceSyncRef.current = syncKey
    setSelectedModelId(nextModelId)
    handleConfigChange(nextConfigId)
  }, [workspaceModelId, models, configs, loading])

  async function handleBasicSectionChange(nextSection) {
    setBasicSection(nextSection)
    if (nextSection === 'customPricing' && selectedModelId) {
      await handleCustomModelChange(selectedModelId)
    }
  }

  function updateModelDraft(key, value) {
    if (!selectedModel) return
    setModelDrafts((prev) => ({
      ...prev,
      [selectedModel.id]: {
        ...(prev[selectedModel.id] || initModelDraft(selectedModel)),
        [key]: value,
      },
    }))
  }

  function toggleTelegramEvent(eventName) {
    if (!selectedModel || !selectedDraft) return
    const current = new Set(selectedDraft.telegram_alert_events || [])
    if (current.has(eventName)) current.delete(eventName)
    else current.add(eventName)
    updateModelDraft('telegram_alert_events', TELEGRAM_ALERT_EVENTS.filter((item) => current.has(item)))
  }

  async function saveModelSettings(event) {
    event.preventDefault()
    if (!selectedModel || !selectedDraft) return
    setSavingModel(true)
    setStatus('')
    setError('')
    const maxPrice = selectedDraft.max_message_price === '' ? null : Number(selectedDraft.max_message_price)
    const defaultConfigId = selectedDraft.default_config_id === '' ? null : Number(selectedDraft.default_config_id)
    const payload = {
      description: selectedDraft.description,
      timezone: selectedDraft.timezone,
      automation_mode: selectedDraft.automation_mode,
      bot_paused: readBool(selectedDraft.bot_paused),
      max_message_price: Number.isFinite(maxPrice) ? maxPrice : null,
      telegram_chat_id: selectedDraft.telegram_chat_id,
      telegram_alerts_enabled: selectedDraft.telegram_alerts_enabled,
      telegram_alert_events: selectedDraft.telegram_alert_events,
    }
    if (canManageModelDefaultConfig) {
      payload.default_config_id = Number.isFinite(defaultConfigId) ? defaultConfigId : null
    }
    if (String(selectedDraft.telegram_bot_token || '').trim()) payload.telegram_bot_token = selectedDraft.telegram_bot_token.trim()

    try {
      const updated = await updateModelAccountDescription(selectedModel.id, payload)
      setModels((prev) => prev.map((model) => (
        model.id === selectedModel.id
          ? {
              ...model,
              ...updated,
              ...(canManageModelDefaultConfig ? { default_config_id: Number.isFinite(defaultConfigId) ? defaultConfigId : null } : {}),
              telegram_bot_token_set: model.telegram_bot_token_set || Boolean(payload.telegram_bot_token),
            }
          : model
      )))
      updateModelDraft('telegram_bot_token', '')
      setStatus('Model settings saved')
    } catch (err) {
      setError(getApiErrorMessage(err, 'Failed to save model settings'))
    } finally {
      setSavingModel(false)
    }
  }

  function updateSetting(key, value) {
    setSettingsDraft((prev) => ({ ...prev, [key]: value }))
  }

  async function saveConfigSettings(event) {
    event.preventDefault()
    setSavingConfig(true)
    setStatus('')
    setError('')
    setWarnings([])
    try {
      const result = await updateAgencySafeConfig({ settings: normalizeSettingsPayload(settingsDraft) }, selectedConfigId || null)
      applyConfig(result?.config || result, selectedConfigId)
      setWarnings(result?.warnings || [])
      setStatus('Configuration settings saved')
    } catch (err) {
      setError(getApiErrorMessage(err, 'Failed to save configuration settings'))
    } finally {
      setSavingConfig(false)
    }
  }

  if (loading) return <LoadingBlock label="Loading settings..." />

  return (
    <div className="v2-stack v2-settings-page">
      <div className="v2-metrics-grid">
        <MetricCard label="Models" value={models.length} detail={`${configuredModels} have profile and timezone`} />
        <MetricCard label="Paused bots" value={pausedModels} detail="Model-level automation stop" tone={pausedModels ? 'warn' : 'neutral'} />
        <MetricCard label="Configuration" value={activeConfigName} detail="Safe editable config" />
        <MetricCard label="Settings mode" value={tab === 'basic' ? 'Basic' : 'Advanced'} detail="Separated by operational risk" tone={tab === 'basic' ? 'good' : 'warn'} />
        <MetricCard label="Warnings" value={warnings.length} detail="After last save" tone={warnings.length ? 'warn' : 'neutral'} />
      </div>

      {error ? <div className="v2-error">{error}</div> : null}
      {status ? <div className="v2-info">{status}</div> : null}
      {warnings.length ? <div className="v2-warning-card">{warnings.map((warning, index) => <p key={`${warning}-${index}`}>{String(warning)}</p>)}</div> : null}

      <div className="v2-tabs v2-settings-tabs">
        <button type="button" className={tab === 'basic' ? 'is-active' : ''} onClick={() => setTab('basic')}>Basic</button>
        <button type="button" className={tab === 'advanced' ? 'is-active' : ''} onClick={() => setTab('advanced')}>Advanced</button>
      </div>

      {tab === 'basic' ? (
        <BasicSettingsTab
          section={basicSection}
          setSection={handleBasicSectionChange}
          models={models}
          selectedModel={selectedModel}
          selectedModelId={selectedModelId}
          handleCustomModelChange={handleCustomModelChange}
          draft={selectedDraft}
          updateModelDraft={updateModelDraft}
          toggleTelegramEvent={toggleTelegramEvent}
          saveModelSettings={saveModelSettings}
          savingModel={savingModel}
          configs={configs}
          selectedConfigId={selectedConfigId}
          handleConfigChange={handleConfigChange}
          settingsDraft={settingsDraft}
          updateSetting={updateSetting}
          saveConfigSettings={saveConfigSettings}
          savingConfig={savingConfig}
          canManageModelDefaultConfig={canManageModelDefaultConfig}
        />
      ) : (
        <AdvancedSettingsTab
          section={advancedSection}
          setSection={setAdvancedSection}
          configs={configs}
          selectedConfigId={selectedConfigId}
          handleConfigChange={handleConfigChange}
          settingsDraft={settingsDraft}
          updateSetting={updateSetting}
          saveConfigSettings={saveConfigSettings}
          savingConfig={savingConfig}
        />
      )}
    </div>
  )
}

function BasicSettingsTab({ section, setSection, models, selectedModel, selectedModelId, handleCustomModelChange, draft, updateModelDraft, toggleTelegramEvent, saveModelSettings, savingModel, configs, selectedConfigId, handleConfigChange, settingsDraft, updateSetting, saveConfigSettings, savingConfig, canManageModelDefaultConfig }) {
  const { t } = useI18n()
  const subnav = [
    ['model', 'Model setup', 'Profile, timezone, automation and price caps.'],
    ['communication', 'Communication', 'Safe prompt defaults and language behavior.'],
    ['customPricing', 'Custom pricing', 'Rates, markups, negotiation floor and tip deposit.'],
    ['monetizationRoutes', t('agencySettings.tabs.monetizationRoutes'), t('agencySettings.monetizationRoutes.navDescription')],
    ['alternativeMonetization', 'Referrals & CPA', 'Cross-profile and dormant-fan partner campaigns.'],
    ['alerts', 'Alerts', 'Telegram routing and event selection.'],
  ]

  return (
    <div className="v2-settings-layout">
      <SettingsSubnav items={subnav} active={section} onSelect={setSection} />

      <div className="v2-stack">
        {section === 'model' ? (
          <>
            <Section eyebrow="Basic" title="Model operating setup" description="Required fields an operator must understand before a model goes live.">
              {!selectedModel || !draft ? (
                <EmptyState title="No model selected" description="Create or connect a model first." />
              ) : (
                <form className="v2-settings-form" onSubmit={saveModelSettings}>
                  <div className="v2-form-grid">
                    <label className="v2-field">
                      <span>Model</span>
                      <select value={selectedModelId} onChange={(event) => handleCustomModelChange(event.target.value)}>
                        <option value="">Select model</option>
                        {models.map((model) => <option key={model.id} value={model.id}>{modelTitle(model)}</option>)}
                      </select>
                    </label>
                    <label className="v2-field">
                      <span>Timezone</span>
                      <input value={draft.timezone} onChange={(event) => updateModelDraft('timezone', event.target.value)} placeholder="America/New_York" />
                    </label>
                    <label className="v2-field">
                      <span>Automation mode</span>
                      <select value={draft.automation_mode} onChange={(event) => updateModelDraft('automation_mode', event.target.value)}>
                        {AUTOMATION_MODE_OPTIONS.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
                      </select>
                    </label>
                    <label className="v2-field">
                      <span>Default graph config</span>
                      {canManageModelDefaultConfig ? (
                        <select value={draft.default_config_id} onChange={(event) => updateModelDraft('default_config_id', event.target.value)}>
                          <option value="">Use active global config</option>
                          {configs.map((config) => (
                            <option key={config.id} value={config.id}>{config.name || `Config #${config.id}`}{config.is_active ? ' (active)' : ''}</option>
                          ))}
                        </select>
                      ) : (
                        <input value={configDisplayName(configs, draft.default_config_id)} readOnly />
                      )}
                    </label>
                    <label className="v2-field">
                      <span>Max message price</span>
                      <input type="number" min="3" max="200" value={draft.max_message_price} onChange={(event) => updateModelDraft('max_message_price', event.target.value)} placeholder="Default" />
                    </label>
                    <label className="v2-field v2-field-wide">
                      <span>Model profile</span>
                      <textarea value={draft.description} onChange={(event) => updateModelDraft('description', event.target.value)} rows={6} placeholder="Persona, tone, boundaries, sales style and account context." />
                    </label>
                  </div>
                  <div className="v2-card-actions">
                    <StatusPill tone={readBool(draft.bot_paused) ? 'bad' : 'good'}>
                      {readBool(draft.bot_paused) ? 'Bot paused' : 'Bot live'}
                    </StatusPill>
                    <button
                      type="button"
                      className="v2-btn v2-btn-secondary"
                      onClick={() => updateModelDraft('bot_paused', !readBool(draft.bot_paused))}
                    >
                      {readBool(draft.bot_paused) ? 'Resume bot' : 'Pause bot'}
                    </button>
                    <button type="submit" className="v2-btn v2-btn-primary" disabled={savingModel}>{savingModel ? 'Saving...' : 'Save model setup'}</button>
                  </div>
                </form>
              )}
            </Section>
            <Section eyebrow="Optional memory" title="Model bible" description="Long-form facts are stored separately from the required model profile and are only retrieved when relevant.">
              {selectedModel ? (
                <ModelBiblePanel modelId={selectedModel.id} variant="v2" />
              ) : (
                <EmptyState title="No model selected" description="Create or connect a model first." />
              )}
            </Section>
          </>
        ) : null}

        {section === 'communication' ? (
          <Section eyebrow="Basic" title="Safe communication defaults" description="Agency-level defaults that operators can understand without opening graph internals.">
            <ConfigSelector configs={configs} selectedConfigId={selectedConfigId} onChange={handleConfigChange} />
            <form className="v2-settings-form" onSubmit={saveConfigSettings}>
              <SettingsFields keys={BASIC_SETTING_KEYS} settingsDraft={settingsDraft} updateSetting={updateSetting} />
              <div className="v2-card-actions">
                <button type="submit" className="v2-btn v2-btn-primary" disabled={savingConfig}>{savingConfig ? 'Saving...' : 'Save communication defaults'}</button>
              </div>
            </form>
          </Section>
        ) : null}

        {section === 'customPricing' ? (
          <Section eyebrow="Sales" title="Custom content" description="Choose a model first. Its assigned graph config stores that model's production capabilities, pricing and deposit rules.">
            <CustomModelConfigSelector
              models={models}
              selectedModel={selectedModel}
              selectedModelId={selectedModelId}
              selectedConfigId={selectedConfigId}
              configs={configs}
              onModelChange={handleCustomModelChange}
            />
            <form className="v2-settings-form" onSubmit={saveConfigSettings}>
              <div className="v2-stack">
                <div>
                  <h3>Production capabilities</h3>
                  <p>These settings become hard route and quote limits after capability enforcement is enabled.</p>
                </div>
                {CUSTOM_CAPABILITY_GROUPS.map((group) => (
                  <div key={group.title} className="v2-stack">
                    <div>
                      <h4>{group.title}</h4>
                    </div>
                    <SettingsFields keys={group.keys} settingsDraft={settingsDraft} updateSetting={updateSetting} />
                  </div>
                ))}
                <div>
                  <h3>Pricing and deposit</h3>
                  <p>The quote preview uses the same capability limits and price formula as live conversations.</p>
                </div>
                <SettingsFields keys={CUSTOM_PRICE_SETTING_KEYS} settingsDraft={settingsDraft} updateSetting={updateSetting} />
              </div>
              <div className="v2-card-actions">
                <button type="submit" className="v2-btn v2-btn-primary" disabled={savingConfig}>{savingConfig ? 'Saving...' : 'Save custom settings'}</button>
              </div>
            </form>
            <CustomPricingPreview configId={selectedConfigId || null} settingsDraft={settingsDraft} />
          </Section>
        ) : null}

        {section === 'monetizationRoutes' ? (
          <Section
            eyebrow={t('agencySettings.monetizationRoutes.eyebrow')}
            title={t('agencySettings.monetizationRoutes.title')}
            description={t('agencySettings.monetizationRoutes.description')}
          >
            <ConfigSelector configs={configs} selectedConfigId={selectedConfigId} onChange={handleConfigChange} />
            <form className="v2-settings-form" onSubmit={saveConfigSettings}>
              <MonetizationRoutesSettings
                settingsDraft={settingsDraft}
                onChange={updateSetting}
                variant="v2"
                t={t}
              />
              <div className="v2-card-actions">
                <button type="submit" className="v2-btn v2-btn-primary" disabled={savingConfig}>
                  {savingConfig ? t('common.saving') : t('agencySettings.monetizationRoutes.save')}
                </button>
              </div>
            </form>
          </Section>
        ) : null}

        {section === 'alternativeMonetization' ? (
          <Section
            eyebrow="Alternative monetization"
            title="Cross-profile referrals and CPA"
            description="Configure approved destinations and conservative eligibility for persistent non-buyers and dormant fans. The feature remains off until explicitly enabled."
          >
            <ConfigSelector configs={configs} selectedConfigId={selectedConfigId} onChange={handleConfigChange} />
            <form className="v2-settings-form" onSubmit={saveConfigSettings}>
              <AlternativeMonetizationSettings settingsDraft={settingsDraft} onChange={updateSetting} />
              <div className="v2-card-actions">
                <button type="submit" className="v2-btn v2-btn-primary" disabled={savingConfig}>{savingConfig ? 'Saving...' : 'Save referral settings'}</button>
              </div>
            </form>
          </Section>
        ) : null}

        {section === 'alerts' ? (
          <Section eyebrow="Basic" title="Operator alerts" description="Notifications are kept separate from model profile and prompt defaults.">
            {!selectedModel || !draft ? (
              <EmptyState title="No model selected" description="Create or connect a model first." />
            ) : (
              <form className="v2-settings-form" onSubmit={saveModelSettings}>
                <label className="v2-field">
                  <span>Model</span>
                  <select value={selectedModelId} onChange={(event) => handleCustomModelChange(event.target.value)}>
                    <option value="">Select model</option>
                    {models.map((model) => <option key={model.id} value={model.id}>{modelTitle(model)}</option>)}
                  </select>
                </label>
                <div className="v2-settings-grid v2-settings-grid-two">
                  <article className="v2-setting-field-card">
                    <div>
                      <strong>Telegram alerts</strong>
                      <p>Operator notifications for important automation events.</p>
                    </div>
                    <label className="v2-check-field">
                      <input type="checkbox" checked={Boolean(draft.telegram_alerts_enabled)} onChange={(event) => updateModelDraft('telegram_alerts_enabled', event.target.checked)} />
                      <span>Enable alerts for this model</span>
                    </label>
                    <label className="v2-field">
                      <span>Telegram chat id</span>
                      <input value={draft.telegram_chat_id} onChange={(event) => updateModelDraft('telegram_chat_id', event.target.value)} placeholder="-100..." />
                    </label>
                    <label className="v2-field">
                      <span>Bot token</span>
                      <input value={draft.telegram_bot_token} onChange={(event) => updateModelDraft('telegram_bot_token', event.target.value)} placeholder={selectedModel.telegram_bot_token_set ? 'Token is already set' : 'Paste token'} />
                    </label>
                  </article>

                  <article className="v2-setting-field-card">
                    <div>
                      <strong>Alert events</strong>
                      <p>Choose which events should interrupt operators.</p>
                    </div>
                    <div className="v2-check-list">
                      {TELEGRAM_ALERT_EVENTS.map((eventName) => (
                        <label key={eventName} className="v2-mini-check">
                          <input type="checkbox" checked={(draft.telegram_alert_events || []).includes(eventName)} onChange={() => toggleTelegramEvent(eventName)} />
                          <span>{eventName}</span>
                        </label>
                      ))}
                    </div>
                  </article>
                </div>
                <div className="v2-card-actions">
                  <button type="submit" className="v2-btn v2-btn-primary" disabled={savingModel}>{savingModel ? 'Saving...' : 'Save alert settings'}</button>
                </div>
              </form>
            )}
          </Section>
        ) : null}
      </div>
    </div>
  )
}

function AdvancedSettingsTab({ section, setSection, configs, selectedConfigId, handleConfigChange, settingsDraft, updateSetting, saveConfigSettings, savingConfig }) {
  const subnav = [
    ['proactive', 'Proactive', 'Timing and dispatch limits.'],
    ['events', 'Platform events', 'Provider webhook triggers and cooldowns.'],
    ['history', 'Reactivation settings', 'Old chat discovery and retry rules.'],
    ['timewasters', 'Timewasters', 'Long unpaid high-tension chats.'],
    ['reactivationQueue', 'Reactivation queue', 'Review and dispatch candidates.'],
    ['l4', 'L4 variables', 'Relationship and monetization context.'],
    ['transitions', 'Transitions', 'Graph branch conditions.'],
    ['conditionalPrompts', 'Conditional prompts', 'Extra prompts by condition.'],
  ]
  const groupsBySection = {
    proactive: ADVANCED_GROUPS[0],
    events: ADVANCED_GROUPS[1],
    history: ADVANCED_GROUPS[2],
    timewasters: ADVANCED_GROUPS[3],
  }
  const group = groupsBySection[section]

  if (!group) {
    const tool = ADVANCED_TOOL_LINKS[section] || ADVANCED_TOOL_LINKS.l4
    return (
      <div className="v2-settings-layout">
        <SettingsSubnav items={subnav} active={section} onSelect={setSection} />
        <div className="v2-stack">
          <AdvancedToolPanel tool={tool} />
        </div>
      </div>
    )
  }

  return (
    <div className="v2-settings-layout">
      <SettingsSubnav items={subnav} active={section} onSelect={setSection} />

      <div className="v2-stack">
        <Section eyebrow="Advanced" title={group.title} description={group.description}>
          <ConfigSelector configs={configs} selectedConfigId={selectedConfigId} onChange={handleConfigChange} />
          <div className="v2-warning-card">
            <strong>Advanced area</strong>
            <p>Changes here can increase provider load, alter proactive behavior, or send messages at scale. Use small increments and review impact after saving.</p>
          </div>
          <form className="v2-settings-form" onSubmit={saveConfigSettings}>
            <div className="v2-advanced-settings-group">
              <div className="v2-advanced-settings-head">
                <h3>{group.title}</h3>
                <p>{group.description}</p>
              </div>
              <SettingsFields keys={group.keys} settingsDraft={settingsDraft} updateSetting={updateSetting} />
            </div>
            <div className="v2-card-actions">
              <button type="submit" className="v2-btn v2-btn-secondary" disabled={savingConfig}>{savingConfig ? 'Saving...' : `Save ${group.title.toLowerCase()}`}</button>
            </div>
          </form>
        </Section>
      </div>
    </div>
  )
}

function AdvancedToolPanel({ tool }) {
  return (
    <Section eyebrow="Advanced editor" title={tool.title} description={tool.description}>
      <div className="v2-settings-grid v2-settings-grid-two">
        <article className="v2-setting-field-card">
          <div className="v2-setting-field-head">
            <div>
              <span>agency_settings</span>
              <strong>{tool.title}</strong>
            </div>
            <StatusPill tone="warn">full editor</StatusPill>
          </div>
          <p>{tool.description}</p>
          <div className="v2-check-list">
            {tool.bullets.map((item) => (
              <span key={item} className="v2-mini-check">{item}</span>
            ))}
          </div>
          <div className="v2-card-actions">
            <Link className="v2-btn v2-btn-primary" to={tool.href}>{tool.action}</Link>
            <Link className="v2-btn v2-btn-ghost" to="/v2/agency-settings">Open all agency tools</Link>
          </div>
        </article>
        <article className="v2-setting-field-card">
          <div className="v2-setting-field-head">
            <div>
              <span>compatibility</span>
              <strong>Shared with classic</strong>
            </div>
            <StatusPill tone="good">production path</StatusPill>
          </div>
          <p>This opens the same production-tested agency settings editor used by classic, directly on the selected section.</p>
          <em>Keeping one editor prevents L4, reactivation and transition behavior from drifting between interfaces.</em>
        </article>
      </div>
    </Section>
  )
}

function SettingsSubnav({ items, active, onSelect }) {
  return (
    <aside className="v2-settings-subnav">
      {items.map(([key, label, description]) => (
        <button key={key} type="button" className={active === key ? 'is-active' : ''} onClick={() => onSelect(key)}>
          <strong>{label}</strong>
          <span>{description}</span>
        </button>
      ))}
    </aside>
  )
}

function ConfigSelector({ configs, selectedConfigId, onChange }) {
  return (
    <div className="v2-config-selector">
      <label className="v2-field">
        <span>Configuration</span>
        <select value={selectedConfigId} onChange={(event) => onChange(event.target.value)}>
          {configs.length ? configs.map((config) => (
            <option key={config.id} value={config.id}>{config.name}{config.is_active ? ' (active)' : ''}</option>
          )) : <option value="">Active configuration</option>}
        </select>
      </label>
      <StatusPill tone="warn">safe config</StatusPill>
    </div>
  )
}

function CustomModelConfigSelector({ models, selectedModel, selectedModelId, selectedConfigId, configs, onModelChange }) {
  const activeConfig = configs.find((config) => config.is_active)
  const effectiveConfigId = String(
    selectedModel?.default_config_id ?? activeConfig?.id ?? selectedConfigId ?? '',
  )
  const effectiveConfig = configs.find((config) => String(config.id) === effectiveConfigId)
  const configName = effectiveConfig?.name
    || (String(selectedConfigId) === effectiveConfigId ? configDisplayName(configs, selectedConfigId) : '')
    || `Config #${effectiveConfigId}`
  const modelsUsingConfig = models.filter((model) => String(
    model.default_config_id ?? activeConfig?.id ?? '',
  ) === effectiveConfigId)
  const usesGlobalConfig = selectedModel?.default_config_id == null
  const sharedModels = modelsUsingConfig.filter((model) => String(model.id) !== String(selectedModelId))

  return (
    <div className="v2-custom-model-context" data-testid="custom-model-config-selector">
      <label className="v2-field">
        <span>Model whose custom settings you are editing</span>
        <select value={selectedModelId} onChange={(event) => onModelChange(event.target.value)}>
          {models.map((model) => {
            const modelConfigId = model.default_config_id ?? activeConfig?.id ?? ''
            const modelConfig = configs.find((config) => String(config.id) === String(modelConfigId))
            const modelConfigName = modelConfig?.name || (modelConfigId ? `Config #${modelConfigId}` : 'Active global config')
            return (
              <option key={model.id} value={model.id}>
                {modelTitle(model)} — {modelConfigName}
              </option>
            )
          })}
        </select>
      </label>

      <div className="v2-custom-model-context-summary">
        <span>Editing</span>
        <strong>{selectedModel ? modelTitle(selectedModel) : 'No model selected'}</strong>
        <p>Assigned graph config: <b>{configName}</b>{effectiveConfigId ? ` (#${effectiveConfigId})` : ''}</p>
        {usesGlobalConfig ? (
          <p className="is-warning">This model uses the active global config. Saving here also changes every model that uses the global config.</p>
        ) : sharedModels.length ? (
          <p className="is-warning">This config is shared with {sharedModels.map(modelTitle).join(', ')}. Saving here changes their custom settings too.</p>
        ) : (
          <p className="is-safe">This config is assigned only to this model.</p>
        )}
        <p className="v2-custom-model-context-hint">To change the assigned config, open Model setup, select this model and update Default graph config.</p>
      </div>
    </div>
  )
}

function configDisplayName(configs, configId) {
  if (configId == null || configId === '') return 'Use active global config'
  const match = configs.find((config) => String(config.id) === String(configId))
  return match?.name || `Config #${configId}`
}

function SettingsFields({ keys, settingsDraft, updateSetting }) {
  return (
    <div className="v2-settings-grid">
      {keys.map((key) => (
        <SettingField key={key} keyName={key} value={settingsDraft[key]} onChange={(value) => updateSetting(key, value)} />
      ))}
    </div>
  )
}

function SettingField({ keyName, value, onChange }) {
  const meta = SETTING_META[keyName] || {
    label: keyName,
    description: 'Configuration setting.',
    recommended: 'Review before changing.',
    type: BOOLEAN_SETTING_KEYS.has(keyName) ? 'boolean' : LIST_SETTING_KEYS.has(keyName) ? 'list' : 'text',
    risk: 'Medium',
  }

  return (
    <article className="v2-setting-field-card">
      <div className="v2-setting-field-head">
        <div>
          <span>{keyName}</span>
          <strong>{meta.label}</strong>
        </div>
        <StatusPill tone={riskTone(meta.risk)}>{meta.risk}</StatusPill>
      </div>
      <p>{meta.description}</p>
      <em>Recommended: {meta.recommended}</em>
      <SettingInput meta={meta} value={value} onChange={onChange} />
    </article>
  )
}

function SettingInput({ meta, value, onChange }) {
  if (meta.type === 'boolean') {
    return (
      <label className="v2-check-field">
        <input type="checkbox" checked={readBool(value)} onChange={(event) => onChange(event.target.checked)} />
        <span>{readBool(value) ? 'Enabled' : 'Disabled'}</span>
      </label>
    )
  }

  if (meta.type === 'textarea') {
    return (
      <label className="v2-field">
        <span>Value</span>
        <textarea value={value ?? ''} onChange={(event) => onChange(event.target.value)} rows={5} placeholder={meta.placeholder || ''} />
      </label>
    )
  }

  if (meta.type === 'checklist') {
    const selected = new Set(Array.isArray(value) ? value : [])
    return (
      <div className="v2-check-list">
        {(meta.options || []).map(([optionValue, optionLabel]) => (
          <label key={optionValue} className="v2-mini-check">
            <input
              type="checkbox"
              checked={selected.has(optionValue)}
              onChange={(event) => {
                const next = new Set(selected)
                if (event.target.checked) next.add(optionValue)
                else next.delete(optionValue)
                onChange(Array.from(next))
              }}
            />
            <span>{optionLabel}</span>
          </label>
        ))}
      </div>
    )
  }

  if (meta.type === 'select') {
    return (
      <label className="v2-field">
        <span>Value</span>
        <select value={value ?? meta.options?.[0]?.[0] ?? ''} onChange={(event) => onChange(event.target.value)}>
          {(meta.options || []).map(([optionValue, optionLabel]) => <option key={optionValue} value={optionValue}>{optionLabel}</option>)}
        </select>
      </label>
    )
  }

  return (
    <label className="v2-field">
      <span>Value</span>
      <input
        type={meta.type === 'number' ? 'number' : 'text'}
        min={meta.min}
        max={meta.max}
        step={meta.step}
        value={value ?? ''}
        onChange={(event) => onChange(event.target.value)}
        placeholder={meta.placeholder || ''}
      />
    </label>
  )
}

function initModelDraft(model = {}) {
  const events = Array.isArray(model.telegram_alert_events)
    ? model.telegram_alert_events.filter((eventName) => TELEGRAM_ALERT_EVENTS.includes(eventName))
    : [...TELEGRAM_ALERT_EVENTS]
  return {
    description: model.account_description || '',
    timezone: model.timezone || '',
    automation_mode: AUTOMATION_MODE_OPTIONS.some(([value]) => value === model.automation_mode) ? model.automation_mode : 'full_auto',
    bot_paused: readBool(model.bot_paused),
    default_config_id: model.default_config_id == null ? '' : String(model.default_config_id),
    max_message_price: model.max_message_price == null ? '' : String(model.max_message_price),
    telegram_alerts_enabled: readBool(model.telegram_alerts_enabled),
    telegram_chat_id: model.telegram_chat_id || '',
    telegram_bot_token: '',
    telegram_alert_events: events,
  }
}

function readBool(value) {
  if (typeof value === 'boolean') return value
  if (typeof value === 'number') return value !== 0
  if (typeof value === 'string') return !['', '0', 'false', 'no', 'off'].includes(value.trim().toLowerCase())
  return Boolean(value)
}

function toCsv(value) {
  if (Array.isArray(value)) return value.join(', ')
  return String(value || '')
}

function csvToList(value) {
  if (Array.isArray(value)) return value.map((item) => String(item).trim()).filter(Boolean)
  return String(value || '').split(',').map((item) => item.trim()).filter(Boolean)
}

function normalizeSettingsPayload(settings) {
  const out = {}
  for (const [key, value] of Object.entries(settings || {})) {
    if (BOOLEAN_SETTING_KEYS.has(key)) {
      out[key] = readBool(value)
    } else if (CHECKLIST_SETTING_KEYS.has(key)) {
      out[key] = Array.isArray(value) ? value : []
    } else if (LIST_SETTING_KEYS.has(key)) {
      out[key] = csvToList(value)
    } else if (['prompt_custom_safe_instructions', 'prompt_tone_style', 'prompt_primary_language'].includes(key) || MONETIZATION_ROUTE_STRING_SETTING_KEYS.has(key) || ALTERNATIVE_MONETIZATION_STRING_KEYS.has(key)) {
      out[key] = String(value ?? '').trim()
    } else if (key === 'prompt_emoji_level' || key === 'prompt_message_length') {
      out[key] = String(value ?? '').trim() || 'auto'
    } else {
      const numberValue = Number(value)
      out[key] = Number.isFinite(numberValue) && String(value).trim() !== '' ? numberValue : value
    }
  }
  return out
}

function riskTone(risk) {
  if (String(risk).toLowerCase() === 'high') return 'bad'
  if (String(risk).toLowerCase() === 'medium') return 'warn'
  return 'good'
}
